import {gql} from '@apollo/client';
import {noop} from '@benchling/js-utils';
import {css, StyleSheet} from 'aphrodite';
import React, {useState} from 'react';
import isEqual from 'react-fast-compare';
import _ from 'underscore';

import * as Analytics from 'analytics/Analytics';
import {assert} from 'Asserts';
import DataConnector from 'connect/DataConnector';
import * as MeasurementConstants from 'constants/MeasurementConstants';
import {NavStoreRegistry} from 'constants/RegistryConstants';
import {
  AccessibleGridCell,
  FullyAccessibleGrid,
  GridSummary,
  isFullyAccessibleGridGraphQL,
} from 'constants/SampleConstants';
import {ActionOrigin} from 'graphql-client/__generated/graphqlGlobalTypes';
import {QueryResultType, useBenchlingQuery, useSkippableBenchlingQuery} from 'graphql-client/BenchlingQuery';
import {getFragmentWrapper} from 'graphql-client/declarative-translations/external/getFragmentWrapper';
import {graphQLArrayToREST, graphQLToREST} from 'graphql-client/declarative-translations/external/translate';
import {accessibleObjs, assertIsAccessible, isInaccessible} from 'graphql-client/InaccessibleInfo';
import * as InventoryUnitUtil from 'inventory/InventoryUnitUtil';
import {getRestrictedSampleUsersDisplayText} from 'inventory/sample-status/RestrictedSampleUsersTextUtil';
import {SampleControlsFeatureLevel} from 'inventory/sample-status/SampleControlsFeatureLevel';
import {hasMinimumSCRFeatureLevel} from 'inventory/sample-status/SampleControlsFeatureLevelUtil';
import {
  checkSamplesForStatusWarnings,
  SampleStatusWarning,
} from 'inventory/sample-status/SampleStatusWarning';
import {BaseSampleStatusWarningTrackingParams} from 'inventory/sample-status/SampleStatusWarningTracking';
import NameTemplateInfoBanner from 'name-templates/NameTemplateInfoBanner';
import NavStore from 'nav/NavStore';
import notifications from 'notifications';
import CreatePlatePane, {
  CreatePlatePaneState,
  DEFAULT_CREATE_PLATE_PANE_STATE,
} from 'samples/CreatePlatePane';
import ExistingPlatePane, {
  ExistingPlatePaneState,
  ExistingPlatePaneWithSetterState,
} from 'samples/ExistingPlatePane';
import {TransferPlatesModalDestinationPlateQuery} from 'samples/grids/__generated/TransferPlatesModalDestinationPlateQuery';
import {TransferPlatesModalSourcePlateQuery} from 'samples/grids/__generated/TransferPlatesModalSourcePlateQuery';
import {
  formatGridCoordinates,
  getContainerLabelWithQuantity,
  getContainerLabelWithQuantityGraphQL,
} from 'samples/SampleUtil';
import * as StorableActions from 'samples/StorableActions';
import * as StorableModalsUtil from 'samples/StorableModalsUtil';
import {GridSchema, PLATE_SCHEMA_TYPE, PlateSchemaType} from 'samples/storables/StorableSchemaConstants';
import ConfirmConcentrationsPane, {
  BaseConfirmConcentrationsPaneState,
  DEFAULT_BASE_CONFIRM_CONCENTRATIONS_STATE,
} from 'samples/transfers/ConfirmConcentrationsPane';
import * as TransferInputPaneStateUtil from 'samples/transfers/input-sample/PaneStateUtil';
import * as TransferActions from 'samples/transfers/TransferActions';
import {
  StorableModalsContainerContent,
  StorableModalsContainerTransfer,
  TransferPaneSampleStatusState,
} from 'samples/transfers/TransferModalUtil';
import * as TransferUtil from 'samples/transfers/TransferUtil';
import {MinimalSourceContainerSummary} from 'samples/transfers/TransferUtil';
import getTestProps from 'test/lib/cypress/getTestProps';
import ModalButtonKey from 'test/lib/cypress/shared/ModalButtonKey';
import InlineBanner, {InlineBannerType} from 'ui-library/banners/InlineBanner';
import Col from 'ui-library/bs/Col';
import FormGroup from 'ui-library/bs/FormGroup';
import Row from 'ui-library/bs/Row';
import {ButtonStyle} from 'ui-library/buttons/Button';
import {CenteredHorizontalThrobber} from 'ui-library/HorizontalThrobber';
import FieldLabel from 'ui-library/inputs/FieldLabel';
import * as LegacyModalContainer from 'ui-library/LegacyModalContainer';
import Modal from 'ui-library/modals/Modal';
import {UnitTypesQuery} from 'units/__generated/UnitTypesQuery';
import {UNIT_TYPES_QUERY} from 'units/UnitsConstants';
import * as logging from 'util/Logging';
import {GenericEvent} from 'util/Types';

import {GridGraphQL} from '../../../graphql-fragments/inventory/__generated/GridGraphQL';
import {PlateGraphQL} from '../../../graphql-fragments/inventory/__generated/PlateGraphQL';
import {PlateSchemaGraphQL} from '../../../graphql-fragments/inventory/schemas/__generated/PlateSchemaGraphQL';
import TransferPlatesQuantityPane, {
  DEFAULT_TRANSFER_PLATES_QUANTITY_STATE,
  TransferPlatesQuantityPaneState,
} from './TransferPlatesQuantityPane';

const CREATE_NEW_PLATE = 'CREATE_NEW_PLATE';
const USE_EXISTING_PLATE = 'USE_EXISTING_PLATE';

/** @package */
export enum TransferPlatesModalStep {
  CHOOSE_PLATE = 'CHOOSE_PLATE',
  CONFIRM_QUANTITY = 'CONFIRM_QUANTITY',
  CONFIRM_CONCENTRATIONS = 'CONFIRM_CONCENTRATIONS',
}

const UPLOAD_WELLS_HELP_TEXT =
  'You must upload a spreadsheet to create wells with your matrix plate. All wells on the matrix ' +
  'plate must be filled to allow transfer. To do so, attach a spreadsheet below with each row ' +
  'specifying a "Container Barcode" and "Destination Well"';

function closeTransferPlatesModal(): void {
  LegacyModalContainer.close(TransferPlatesModal);
}

export function openTransferPlatesModal(props: BaseProps): void {
  LegacyModalContainer.open(
    TransferPlatesModal,
    <ConnectedTransferPlatesModal {...props} onRequestClose={closeTransferPlatesModal} />
  );
}

function BlockingError({errorMessage}: {errorMessage: string}): JSX.Element {
  return (
    <div className={css(styles.errorMessage)}>
      <p className="u-danger-color">{errorMessage}</p>
    </div>
  );
}

interface BaseProps {
  cells: Array<AccessibleGridCell>;
  grid: FullyAccessibleGrid;
  registryId: string;
}

/** @package */
export interface TransferPlatesModalProps extends BaseProps {
  onRequestClose: () => void;
}

interface ConnectedProps {
  registry: NavStoreRegistry;
}

interface QueryingProps {
  existingPlatePaneState: ExistingPlatePaneWithSetterState;
  selectedDestinationPlate: PlateGraphQL | null;
  destinationPlateError?: string | null;
  isLoadingDestinationPlate: boolean;
  isLoadingUnits: boolean;
  sourceCellsWithNameTemplate: Array<Array<boolean>>;
}

type CombinedProps = TransferPlatesModalProps & ConnectedProps & QueryingProps;

/** @package */
export interface TransferPlatesModalState {
  shouldCreatePlate: boolean;
  shouldShowNameTemplateBanner: boolean;
  step: TransferPlatesModalStep;
  isTransferring: boolean;
  errorMessage: string | null;
  // used in ConfirmConcentrationsPane and when sending TransferAction requests to the backend
  containerContents: Array<StorableModalsContainerContent> | null;
  // used only when sending TransferAction requests to the backend
  containerTransfers: Array<StorableModalsContainerTransfer> | null;
  createPlatePaneState: CreatePlatePaneState;
  transferPlatesQuantityPaneState: TransferPlatesQuantityPaneState;
  confirmConcentrationsPaneState: BaseConfirmConcentrationsPaneState;
  inputSampleStatusState: TransferPaneSampleStatusState;
  outputSampleStatusState: TransferPaneSampleStatusState;
}

class TransferPlatesModalConnector extends DataConnector<TransferPlatesModalProps, ConnectedProps> {
  override shouldSubscribeToRedux = false;
  override stores = [NavStore];

  enhanceProps(): ConnectedProps {
    return {
      registry: this.navStore().getSource(this.props.registryId) as NavStoreRegistry,
    };
  }
}

export const TRANSFER_PLATES_MODAL_SOURCE_PLATE_QUERY = gql`
  query TransferPlatesModalSourcePlateQuery($plateId: StrictString!)
  @skipAudit
  @engComponent(name: INVENTORY) {
    plates(ids: [$plateId]) {
      ... on PlateNode {
        id
        cells {
          ... on GridCellNode {
            container {
              ... on ContainerNode {
                id
                hasNameTemplate
              }
              ...InaccessibleInfoGraphQL
            }
          }
        }
      }
      ...InaccessibleInfoGraphQL
    }
  }
  ${getFragmentWrapper('InaccessibleInfo').fragment}
`;

export const TRANSFER_PLATES_MODAL_DESTINATION_PLATE_QUERY = gql`
  query TransferPlatesModalDestinationPlateQuery($plateId: StrictString!)
  @skipAudit
  @engComponent(name: INVENTORY) {
    plates(ids: [$plateId]) {
      ...PlateGraphQL
      ...InaccessibleInfoGraphQL
    }
  }
  ${getFragmentWrapper('Plate').fragment}
  ${getFragmentWrapper('InaccessibleInfo').fragment}
`;

function QueryingTransferPlatesModal(props: TransferPlatesModalProps & ConnectedProps): JSX.Element | null {
  const [selectedGrid, setSelectedGrid] = useState<GridSummary | null>(null);
  const unitTypesQueryResult = useBenchlingQuery<UnitTypesQuery>(UNIT_TYPES_QUERY);
  const destinationPlateQueryResult = useSkippableBenchlingQuery<TransferPlatesModalDestinationPlateQuery>(
    TRANSFER_PLATES_MODAL_DESTINATION_PLATE_QUERY,
    {skip: selectedGrid == null, variables: {plateId: selectedGrid?.id}}
  );
  const sourcePlateQueryResult = useBenchlingQuery<TransferPlatesModalSourcePlateQuery>(
    TRANSFER_PLATES_MODAL_SOURCE_PLATE_QUERY,
    {
      variables: {plateId: props.grid.id},
      fetchPolicy: 'cache-and-network',
      renderCachedDataWhenRefetching: true,
    }
  );

  // Default to false, since:
  // 1) This information won't be used until a destination plate or destination plate schema is selected, so
  // it doesn't make sense to prevent loading the modal while the query is pending.
  // 2) This information is only used to determine whether to render an info banner, so it doesn't make sense
  // to block using the modal if the query errors.
  let cellsWithNameTemplate = Array(props.grid.storableSchema.height).fill(
    Array(props.grid.storableSchema.width).fill(false)
  );

  if (sourcePlateQueryResult.type === QueryResultType.SUCCESS) {
    const sourcePlate = sourcePlateQueryResult.data.plates[0];
    assert(!isInaccessible(sourcePlate), 'Expected source plate to be accessible');
    cellsWithNameTemplate = sourcePlate.cells.map((row) =>
      row.map((cell) => cell != null && !isInaccessible(cell.container) && cell.container.hasNameTemplate)
    );
  } else if (sourcePlateQueryResult.type === QueryResultType.ERROR) {
    logging.warn(`TransferPlatesModalSourcePlateQuery failed for plate: ${props.grid.id}`);
  }

  if (destinationPlateQueryResult.type === QueryResultType.SKIPPED) {
    return (
      <TransferPlatesModal
        {...props}
        selectedDestinationPlate={null}
        isLoadingDestinationPlate={false}
        isLoadingUnits={unitTypesQueryResult.type === QueryResultType.PENDING}
        existingPlatePaneState={{selectedGrid, setSelectedGrid}}
        sourceCellsWithNameTemplate={cellsWithNameTemplate}
      />
    );
  }

  if (destinationPlateQueryResult.type === QueryResultType.PENDING) {
    return (
      <TransferPlatesModal
        {...props}
        selectedDestinationPlate={null}
        isLoadingDestinationPlate={true}
        isLoadingUnits={unitTypesQueryResult.type === QueryResultType.PENDING}
        existingPlatePaneState={{selectedGrid, setSelectedGrid}}
        sourceCellsWithNameTemplate={cellsWithNameTemplate}
      />
    );
  }

  if (destinationPlateQueryResult.type === QueryResultType.ERROR) {
    notifications.error('Could not load destination plate details; try reloading the page.');
    props.onRequestClose();
    return null;
  }

  const plateGraphQL = destinationPlateQueryResult.data.plates[0];

  assert(!isInaccessible(plateGraphQL), 'Expected selected destination plate to be accessible');
  const destinationIsFullyAccessible = isFullyAccessibleGridGraphQL(plateGraphQL);

  return (
    <TransferPlatesModal
      {...props}
      selectedDestinationPlate={destinationIsFullyAccessible ? plateGraphQL : null}
      isLoadingDestinationPlate={false}
      isLoadingUnits={unitTypesQueryResult.type === QueryResultType.PENDING}
      destinationPlateError={
        !destinationIsFullyAccessible ? 'All containers in destination plate must be accessible.' : null
      }
      existingPlatePaneState={{selectedGrid, setSelectedGrid}}
      sourceCellsWithNameTemplate={cellsWithNameTemplate}
    />
  );
}

/** @package */
export class TransferPlatesModal extends React.Component<CombinedProps, TransferPlatesModalState> {
  constructor(props: CombinedProps) {
    super(props);
    this.state = {
      shouldCreatePlate: true,
      shouldShowNameTemplateBanner: false,
      step: TransferPlatesModalStep.CHOOSE_PLATE,
      isTransferring: false,
      errorMessage: null,
      containerContents: null,
      containerTransfers: null,
      createPlatePaneState: {...DEFAULT_CREATE_PLATE_PANE_STATE, registry: props.registry},
      transferPlatesQuantityPaneState: {
        ...DEFAULT_TRANSFER_PLATES_QUANTITY_STATE,
        transferQuantity: {value: null, units: props.cells[0].container.quantity.units},
      },
      confirmConcentrationsPaneState: DEFAULT_BASE_CONFIRM_CONCENTRATIONS_STATE,
      inputSampleStatusState: TransferInputPaneStateUtil.getDefaultTransferPaneSampleStatusState(),
      outputSampleStatusState: TransferInputPaneStateUtil.getDefaultTransferPaneSampleStatusState(),
    };
  }

  override componentDidMount(): void {
    this.checkForInputWarnings();
  }

  override componentDidUpdate(prevProps: CombinedProps, prevState: TransferPlatesModalState): void {
    // Update confirm concentration pane state based on the container transfers & batches
    if (
      !isEqual(
        this._getConcentrationStateDependencies(prevState),
        this._getConcentrationStateDependencies(this.state)
      )
    ) {
      this._updateConcentrationPaneState(this.state);
    }

    if (this.state.step === TransferPlatesModalStep.CHOOSE_PLATE) {
      if (
        prevState.createPlatePaneState.isValidating &&
        !this.state.createPlatePaneState.isValidating &&
        _.isEmpty(this.state.createPlatePaneState.validationErrors)
      ) {
        this.setState({step: TransferPlatesModalStep.CONFIRM_QUANTITY});
      }

      if (!isEqual(prevProps.selectedDestinationPlate, this.props.selectedDestinationPlate)) {
        this.checkForOutputWarnings();
      }

      if (
        !isEqual(prevState.shouldCreatePlate, this.state.shouldCreatePlate) ||
        !isEqual(prevProps.selectedDestinationPlate, this.props.selectedDestinationPlate) ||
        !isEqual(
          prevState.createPlatePaneState.selectedSchema,
          this.state.createPlatePaneState.selectedSchema
        )
      ) {
        this.checkForContainerNameTemplates();
      }
    }

    if (prevState.isTransferring && !this.state.isTransferring && !this.state.errorMessage) {
      this.props.onRequestClose();
    }
  }

  _getConcentrationStateDependencies(state: TransferPlatesModalState): {
    containerTransfers: Array<StorableModalsContainerTransfer> | null;
    containerContents: Array<StorableModalsContainerContent> | null;
    shouldCalculateConcentrations: boolean;
  } {
    return {
      containerTransfers: state.containerTransfers,
      containerContents: state.containerContents,
      shouldCalculateConcentrations: state.confirmConcentrationsPaneState.shouldCalculateConcentrations,
    };
  }

  _updateConcentrationPaneState(state: TransferPlatesModalState): void {
    const relevantState = this._getConcentrationStateDependencies(state);
    const updatedState = StorableModalsUtil.getUpdatedConcentrationPaneState({
      containerTransfers: relevantState.containerTransfers,
      containerContents: relevantState.containerContents,
      shouldCalculateConcentrations: relevantState.shouldCalculateConcentrations,
      inputContainer: null,
      errorMessage: null,
    });
    this.setState({
      confirmConcentrationsPaneState: {...this.state.confirmConcentrationsPaneState, ...updatedState},
    });
  }

  _handleUpdateCreatePlatePane(state: Partial<CreatePlatePaneState>): void {
    this.setState({createPlatePaneState: {...this.state.createPlatePaneState, ...state}});
  }

  _handleUpdateExistingPlatePane(state: Partial<ExistingPlatePaneState>): void {
    this.props.existingPlatePaneState.setSelectedGrid(state.selectedGrid ?? null);
  }

  _handleChangeShouldCreatePlate(e: GenericEvent): void {
    this.setState({shouldCreatePlate: e.target.value === CREATE_NEW_PLATE});
  }

  _handleChangeShouldTransferAllQuantities(e: GenericEvent): void {
    this.setState((oldState) => ({
      ...oldState,
      transferPlatesQuantityPaneState: {
        ...oldState.transferPlatesQuantityPaneState,
        shouldTransferAllQuantities:
          e.target.value === TransferPlatesQuantityPane.QUANTITY_OPTIONS.TRANSFER_ALL,
      },
    }));
  }

  _handleChangeTransferQuantityErrorState = (
    hasTransferQuantityError: boolean,
    wellsWithInsufficientQuantity: Array<MinimalSourceContainerSummary>,
    quantityErrorsAreBlocking: boolean
  ): void => {
    this.setState((oldState) => ({
      ...oldState,
      transferPlatesQuantityPaneState: {
        ...oldState.transferPlatesQuantityPaneState,
        hasTransferQuantityError,
        wellsWithInsufficientQuantity,
        quantityErrorsAreBlocking,
      },
    }));
  };

  _handleChangeTransferQuantity(transferQuantity: MeasurementConstants.LegacyMeasurement): void {
    this.setState((oldState) => ({
      ...oldState,
      transferPlatesQuantityPaneState: {...oldState.transferPlatesQuantityPaneState, transferQuantity},
    }));
  }

  _handleChangeConcentration = (
    containerId: string,
    containedItemId: string,
    concentration: Partial<MeasurementConstants.LegacyMeasurement>
  ): void => {
    assert(
      this.state.containerContents != null,
      'Expected container contents to be non null on concentration pane'
    );
    const index = this.state.containerContents.findIndex(
      (cb) => cb.containedItemId === containedItemId && cb.containerId === containerId
    );
    assert(index !== -1, 'Expected container batch to be present');
    const updatedContainerContent = {
      ...this.state.containerContents[index],
      concentration: {...this.state.containerContents[index].concentration, ...concentration},
    };
    const containerContents = [
      ...this.state.containerContents.slice(0, index),
      updatedContainerContent,
      ...this.state.containerContents.slice(index + 1),
    ];
    this.setState({containerContents});
  };

  _handleUpdateTransferParams = (
    transferParams: {
      containerTransfers: Array<StorableModalsContainerTransfer> | null;
      containerContents: Array<StorableModalsContainerContent> | null;
    },
    clearErrorMessage?: boolean
  ): void => {
    const errorState = clearErrorMessage ? {errorMessage: null} : {};
    this.setState({...this.state, ...errorState, ...transferParams});
  };

  _handleUpdateErrors = (errorMessage: string): void => {
    this.setState({errorMessage});
  };

  checkForInputWarnings = (): void => {
    const containers = this.props.cells.map(({container}) => container);
    const {shouldShowWarning, samples} = checkSamplesForStatusWarnings({
      samples: containers,
      showOnlySamplesWithWarnings: true,
    });

    this.updateSampleStatusState(
      {
        shouldShowStatusWarnings: shouldShowWarning,
        samples: samples ?? null,
        selectedSampleIds: samples?.map(({id}) => id) ?? [],
      },
      'inputSampleStatusState'
    );
  };

  checkForOutputWarnings = (): void => {
    const containers = (this.props.selectedDestinationPlate?.cells ?? []).flatMap((cellRow) =>
      cellRow.flatMap((cell) => cell?.container ?? [])
    );

    const {shouldShowWarning, samples} = checkSamplesForStatusWarnings({
      samples: graphQLArrayToREST('Container', accessibleObjs(containers)),
      showOnlySamplesWithWarnings: true,
    });

    this.updateSampleStatusState(
      {
        shouldShowStatusWarnings: shouldShowWarning,
        samples: samples ?? null,
        selectedSampleIds: samples?.map(({id}) => id) ?? [],
      },
      'outputSampleStatusState'
    );
  };

  checkForContainerNameTemplates = (): void => {
    const destinationContainers = accessibleObjs(
      (this.props.selectedDestinationPlate?.cells ?? []).flatMap((cellRow) =>
        cellRow.flatMap((cell) => cell?.container ?? [])
      )
    );
    const flatSourceContainersHaveNameTemplate = this.props.sourceCellsWithNameTemplate.flatMap((cellRow) =>
      cellRow.flatMap((cellHasNameTemplate) => cellHasNameTemplate)
    );
    const destinationPlateSchemaType = this.state.shouldCreatePlate
      ? this.state.createPlatePaneState.selectedSchema?.plateSchemaType
      : (this.props.selectedDestinationPlate?.storableSchema as PlateSchemaGraphQL | null)?.plateSchemaType;

    // Check basic conditions first
    let mayApplyNameTemplate =
      this.props.grid.storableSchema.plateSchemaType === PLATE_SCHEMA_TYPE.MATRIX_PLATE &&
      destinationPlateSchemaType === PlateSchemaType.MATRIX_PLATE;

    // Check if any transfers are from source container with a name template -> empty destination container
    if (mayApplyNameTemplate) {
      if (this.state.shouldCreatePlate) {
        // If creating a plate, all containers must be empty
        mayApplyNameTemplate = flatSourceContainersHaveNameTemplate.some(
          (cellHasNameTemplate) => cellHasNameTemplate
        );
      } else {
        mayApplyNameTemplate = flatSourceContainersHaveNameTemplate.some(
          (sourceContainerHasNameTemplate, index) =>
            sourceContainerHasNameTemplate &&
            destinationContainers[index] != null &&
            !destinationContainers[index].contents.length
        );
      }
    }

    this.setState({shouldShowNameTemplateBanner: mayApplyNameTemplate});
  };

  updateSampleStatusState = (
    statusState: Partial<TransferPaneSampleStatusState>,
    statusStateKey: 'inputSampleStatusState' | 'outputSampleStatusState'
  ): void => {
    const newStatusState = {
      ...this.state[statusStateKey],
      ...statusState,
    };
    if (statusStateKey === 'inputSampleStatusState') {
      this.setState({inputSampleStatusState: newStatusState});
    } else {
      this.setState({outputSampleStatusState: newStatusState});
    }
  };

  setCanProceedOnSampleStatus = (
    canProceed: boolean,
    statusStateKey: 'inputSampleStatusState' | 'outputSampleStatusState'
  ): void => {
    this.updateSampleStatusState({canProceedOnSampleStatus: canProceed}, statusStateKey);
  };

  sampleStatusWarningTrackingParams: BaseSampleStatusWarningTrackingParams = {
    actionAttempted: 'transfer_plate',
    actionOrigin: ActionOrigin.UI_UNDETERMINED,
    eventGroupId: Analytics.makeAnalyticsEventId(),
  };

  _getSourcePlateSchema(): GridSchema {
    return this.props.grid.storableSchema;
  }

  _getDestinationPlateSchema(): GridSchema | null {
    return this.state.shouldCreatePlate
      ? this.state.createPlatePaneState.selectedSchema
      : this.props.existingPlatePaneState.selectedGrid?.storableSchema || null;
  }

  _getTransferToSelfErrorText(): string | null {
    return this.props.grid.id === this.props.existingPlatePaneState.selectedGrid?.id
      ? 'Source and destination plates cannot be the same.'
      : null;
  }

  _getMismatchedPlateDimensionsErrorText(): string | null {
    const sourceSchema = this._getSourcePlateSchema();
    const destinationSchema = this._getDestinationPlateSchema();
    return destinationSchema != null &&
      (sourceSchema.height !== destinationSchema.height || sourceSchema.width !== destinationSchema.width)
      ? 'Source and destination plate dimensions do not match.'
      : null;
  }

  _areUnitsCompatible(symbols: Array<string | null>): boolean {
    return InventoryUnitUtil.areUnitsCompatibleFromSymbols(symbols);
  }

  _getIncompatibleUnitsErrorTextForWell(
    sourceGridCell: AccessibleGridCell | null,
    destinationGridCell: GridGraphQL['cells'][number][number] | null,
    rowIndex: number,
    columnIndex: number
  ): string | null {
    const sourceUnits = sourceGridCell && sourceGridCell.container.quantity.units;
    const destinationUnits =
      destinationGridCell && assertIsAccessible(destinationGridCell.container).quantityJson.units;

    const transferQuantity = this.state.transferPlatesQuantityPaneState.shouldTransferAllQuantities
      ? (sourceGridCell?.container.quantity ?? {value: null, units: null})
      : this.state.transferPlatesQuantityPaneState.transferQuantity;

    if (this._areUnitsCompatible([sourceUnits, transferQuantity.units, destinationUnits])) {
      return null;
    }

    // Format each error line like this:
    // 'Position A1: Cannot transfer 10 mg from PLT001:A1 (11 mg) to PLT002:A1 (2 L) because the
    // units are incompatible'
    const positionText = `Position ${formatGridCoordinates(rowIndex, columnIndex)}`;
    const transferQuantityText =
      // Error message should not mention transfer quantity in the choose plate pane because the
      // transfer quantity is not specified yet
      this.state.step === TransferPlatesModalStep.CHOOSE_PLATE || transferQuantity.units == null
        ? ''
        : `${transferQuantity.value} ${transferQuantity.units} `;
    const fromSourceContainerText =
      sourceGridCell == null ? '' : `from ${getContainerLabelWithQuantity(sourceGridCell.container)} `;
    const intoDestContainerText =
      destinationGridCell == null
        ? ''
        : `into ${getContainerLabelWithQuantityGraphQL(assertIsAccessible(destinationGridCell.container))}`;
    return (
      `${positionText}: Cannot transfer ` +
      `${transferQuantityText}${fromSourceContainerText}${intoDestContainerText}` +
      ' because the units are incompatible.'
    );
  }

  _getIncompatibleUnitsErrorText(): string | null {
    const sourceCells = this.props.grid.cells;
    const destinationCells = this.state.shouldCreatePlate
      ? null
      : (this.props.selectedDestinationPlate?.cells ?? null);
    assert(
      !destinationCells ||
        (sourceCells.length === destinationCells.length &&
          sourceCells[0].length === destinationCells[0].length),
      'Expected source and destination plates to have same dimensions'
    );

    const errorLines = sourceCells.flatMap((row, i) =>
      row.map((sourceGridCell, j) => {
        const destinationGridCell = destinationCells && destinationCells[i][j];
        return this._getIncompatibleUnitsErrorTextForWell(sourceGridCell, destinationGridCell, i, j);
      })
    );

    return errorLines.filter((errorLine) => errorLine).join('\n');
  }

  _getBlockingErrorText(): string | null {
    return (
      this._getTransferToSelfErrorText() ||
      this._getMismatchedPlateDimensionsErrorText() ||
      this._getIncompatibleUnitsErrorText() ||
      null
    );
  }

  _handleFinishWellSpreadsheetValidation = (errorMessage?: string): void => {
    const validationErrors = errorMessage != null ? {formError: errorMessage} : {};
    this._handleUpdateCreatePlatePane({isValidating: false, validationErrors});
  };

  _handleClickBack(): void {
    switch (this.state.step) {
      case TransferPlatesModalStep.CONFIRM_QUANTITY:
        this.setState({step: TransferPlatesModalStep.CHOOSE_PLATE});
        break;
      case TransferPlatesModalStep.CONFIRM_CONCENTRATIONS:
        this.setState({step: TransferPlatesModalStep.CONFIRM_QUANTITY});
        break;
      default:
        throw Error('Unexpected step');
    }
  }

  _handleClickNext(): void {
    switch (this.state.step) {
      case TransferPlatesModalStep.CHOOSE_PLATE: {
        if (this.state.shouldCreatePlate) {
          const {selectedSchema, selectedContainerSchema, spreadsheetFile, spreadsheetURL} =
            this.state.createPlatePaneState;
          const validationErrors = StorableModalsUtil.getCreatePlateValidationErrors(
            this.state.createPlatePaneState
          );
          this._handleUpdateCreatePlatePane({validationErrors});
          if (_.isEmpty(validationErrors)) {
            if (spreadsheetFile) {
              assert(selectedSchema != null, 'Expected plate schema to be selected');
              assert(selectedContainerSchema != null, 'Expected container schema to be selected');
              assert(spreadsheetURL != null, 'Expected spreadsheetURL to exist');
              this._handleUpdateCreatePlatePane({isValidating: true});
              StorableActions.validateWellSpreadsheet(
                this.props.grid.id,
                selectedSchema.id,
                selectedContainerSchema.id,
                spreadsheetFile.name,
                spreadsheetURL,
                this._handleFinishWellSpreadsheetValidation
              );
            } else {
              this.setState({step: TransferPlatesModalStep.CONFIRM_QUANTITY});
            }
          }
        } else {
          this.setState({step: TransferPlatesModalStep.CONFIRM_QUANTITY});
        }
        break;
      }
      case TransferPlatesModalStep.CONFIRM_QUANTITY: {
        if (this.state.shouldCreatePlate) {
          const {containerTransfers, containerContents} =
            StorableModalsUtil.transferParamsFromCellsToNewPlate(this.props.cells);
          this._handleUpdateTransferParams({containerTransfers, containerContents});
        } else {
          assert(
            this.props.existingPlatePaneState.selectedGrid != null,
            'Expected destination plate to be selected'
          );
          this._handleUpdateTransferParams({containerTransfers: null, containerContents: null});
          TransferActions.calculateConcentrationsGridToGrid(
            this.props.grid.id,
            this.props.existingPlatePaneState.selectedGrid.id,
            this.state.transferPlatesQuantityPaneState.shouldTransferAllQuantities,
            this.state.transferPlatesQuantityPaneState.transferQuantity,
            (transferParams) => {
              this._handleUpdateTransferParams(transferParams, true);
            },
            this._handleUpdateErrors
          );
        }
        this.setState({step: TransferPlatesModalStep.CONFIRM_CONCENTRATIONS});
        break;
      }
      default:
        throw Error('Unexpected step');
    }
  }

  _handleStartTransfer = (): void => {
    this.setState({isTransferring: true});
  };

  _handleFinishTransfer = (errorMessage?: string): void => {
    this.setState({isTransferring: false, errorMessage: errorMessage ?? null});
  };

  _handleTransferPlates(): void {
    assert(
      this.state.containerContents != null,
      'Expected containerContents to be present when transferring'
    );
    if (this.state.shouldCreatePlate) {
      const createPlatePaneStateForTransfer = {
        ...this.state.createPlatePaneState,
        registry: this.state.createPlatePaneState.registry!,
        selectedSchema: this.state.createPlatePaneState.selectedSchema!,
      };
      TransferActions.transferToNewGrid(
        this.props.grid.id,
        createPlatePaneStateForTransfer,
        this.state.transferPlatesQuantityPaneState,
        {containerContents: this.state.containerContents},
        this._handleStartTransfer,
        this._handleFinishTransfer
      );
    } else {
      const existingPlatePaneStateForTransfer = {
        selectedGrid: this.props.existingPlatePaneState.selectedGrid!,
      };
      TransferActions.transferToExistingPlate(
        this.props.grid.id,
        existingPlatePaneStateForTransfer,
        this.state.transferPlatesQuantityPaneState,
        {containerContents: this.state.containerContents},
        this._handleStartTransfer,
        this._handleFinishTransfer
      );
    }
  }

  _getChoosePlateButtonText(): string {
    if (
      this.props.isLoadingDestinationPlate ||
      StorableModalsUtil.isSpreadsheetLoading(this.state.createPlatePaneState)
    ) {
      return 'Loading...';
    }
    if (this.state.createPlatePaneState.isValidating) {
      return 'Validating...';
    }
    return 'Next';
  }

  _getTransferButtonText(): string {
    if (this.state.isTransferring) {
      return 'Transferring...';
    } else {
      return 'Done';
    }
  }

  _isChoosePlateButtonDisabled(): boolean {
    const {selectedSchema, spreadsheetURL} = this.state.createPlatePaneState;
    // When making a new matrix plate, uploading a spreadsheet to create matrix plate wells is required
    const isMatrixSpreadsheetRequired =
      selectedSchema && selectedSchema.plateSchemaType === PLATE_SCHEMA_TYPE.MATRIX_PLATE && !spreadsheetURL;
    if (this.state.shouldCreatePlate) {
      return (
        isMatrixSpreadsheetRequired ||
        StorableModalsUtil.isSpreadsheetLoading(this.state.createPlatePaneState) ||
        this.state.createPlatePaneState.isValidating
      );
    } else {
      return (
        !StorableModalsUtil.isExistingPlatePaneComplete(this.props.existingPlatePaneState) ||
        this.props.isLoadingDestinationPlate ||
        this.props.destinationPlateError != null
      );
    }
  }

  hasBlockingSampleStatusWarnings = (): boolean =>
    (this.state.inputSampleStatusState.shouldShowStatusWarnings &&
      !this.state.inputSampleStatusState.canProceedOnSampleStatus) ||
    (!this.state.shouldCreatePlate &&
      this.state.outputSampleStatusState.shouldShowStatusWarnings &&
      !this.state.outputSampleStatusState.canProceedOnSampleStatus);

  renderPossibleWarning = (
    statusStateKey: 'inputSampleStatusState' | 'outputSampleStatusState'
  ): JSX.Element | null => {
    const sampleStatusState = this.state[statusStateKey];
    return (statusStateKey === 'inputSampleStatusState' || this.props.selectedDestinationPlate != null) &&
      sampleStatusState.shouldShowStatusWarnings &&
      sampleStatusState.samples != null ? (
      <SampleStatusWarning
        sampleIds={sampleStatusState.samples.map(({id}) => id)}
        selectedSampleIds={sampleStatusState.selectedSampleIds}
        setSelectedSampleIds={noop}
        shouldProceed={sampleStatusState.canProceedOnSampleStatus}
        setShouldProceed={(canProceed) => {
          this.setCanProceedOnSampleStatus(canProceed, statusStateKey);
        }}
        trackingParams={this.sampleStatusWarningTrackingParams}
        samples={sampleStatusState.samples}
        canSelectMultiple={false}
      />
    ) : null;
  };

  renderPossibleDestinationWarning = (): JSX.Element | null => {
    if (
      !hasMinimumSCRFeatureLevel(SampleControlsFeatureLevel.V1) ||
      this.props.grid.storableSchema.plateSchemaType === PLATE_SCHEMA_TYPE.FIXED_PLATE
    ) {
      return null;
    }

    if (
      (this.state.shouldCreatePlate &&
        this.state.createPlatePaneState.selectedSchema != null &&
        this.state.createPlatePaneState.selectedSchema?.plateSchemaType !== PlateSchemaType.MATRIX_PLATE) ||
      (!this.state.shouldCreatePlate &&
        this.props.selectedDestinationPlate != null &&
        (this.props.selectedDestinationPlate?.storableSchema as PlateSchemaGraphQL).plateSchemaType !==
          PlateSchemaType.MATRIX_PLATE)
    ) {
      return null;
    }

    let shouldRenderWarning = false;
    if (this.props.selectedDestinationPlate != null) {
      for (const {columnIndex, rowIndex, container} of this.props.cells) {
        const outputContainer = this.props.selectedDestinationPlate.cells[rowIndex][columnIndex]?.container;
        shouldRenderWarning = TransferUtil.willTransferModifyOutputSampleRoles(
          container,
          outputContainer != null && !isInaccessible(outputContainer)
            ? [graphQLToREST('Container', outputContainer)]
            : []
        );
        if (shouldRenderWarning) {
          break;
        }
      }
    }

    return this.state.shouldCreatePlate || shouldRenderWarning ? (
      <InlineBanner
        bannerType={InlineBannerType.INFO}
        key="sample-control"
        text={`Any sample owners and ${getRestrictedSampleUsersDisplayText()} of the source containers will be added to ${
          this.state.shouldCreatePlate ? 'the created' : 'those of the destination'
        } containers.`}
      />
    ) : null;
  };

  override render(): JSX.Element {
    if (this.props.isLoadingUnits) {
      return (
        <Modal
          buttons={[]}
          onRequestClose={this.props.onRequestClose}
          title="Transfer plates"
          dataTestAttrs={{component: 'TransferPlatesModal', element: 'root'}}
        >
          <CenteredHorizontalThrobber />
        </Modal>
      );
    }

    let buttons;
    const {existingPlatePaneState} = this.props;
    const {createPlatePaneState, transferPlatesQuantityPaneState, confirmConcentrationsPaneState} =
      this.state;

    const blockingErrorText = this._getBlockingErrorText();
    const hasBlockingError = Boolean(blockingErrorText);

    if (this.state.step === TransferPlatesModalStep.CHOOSE_PLATE) {
      buttons = [
        {
          text: this._getChoosePlateButtonText(),
          onClick: () => {
            this._handleClickNext();
          },
          style: ButtonStyle.SECONDARY,
          disabled:
            hasBlockingError || this._isChoosePlateButtonDisabled() || this.hasBlockingSampleStatusWarnings(),
          dataTestAttrs: {
            component: 'TransferPlatesModal',
            key: ModalButtonKey.NEXT,
          },
        },
      ];
    } else if (this.state.step === TransferPlatesModalStep.CONFIRM_QUANTITY) {
      buttons = [
        {
          text: 'Back',
          onClick: () => {
            this._handleClickBack();
          },
        },
        {
          text: 'Next',
          onClick: () => {
            this._handleClickNext();
          },
          style: ButtonStyle.SECONDARY,
          disabled:
            hasBlockingError ||
            (!transferPlatesQuantityPaneState.shouldTransferAllQuantities &&
              (!TransferUtil.isValidLegacyMeasurement(transferPlatesQuantityPaneState.transferQuantity) ||
                (transferPlatesQuantityPaneState.quantityErrorsAreBlocking &&
                  transferPlatesQuantityPaneState.hasTransferQuantityError))),
          dataTestAttrs: {
            component: 'TransferPlatesModal',
            key: ModalButtonKey.NEXT,
          },
        },
      ];
    } else {
      buttons = [
        {
          text: 'Back',
          onClick: () => {
            this._handleClickBack();
          },
        },
        {
          text: this._getTransferButtonText(),
          onClick: () => {
            this._handleTransferPlates();
          },
          style: ButtonStyle.SECONDARY,
          disabled: hasBlockingError || !StorableModalsUtil.isConfirmConcentrationsPaneComplete(this.state),
          dataTestAttrs: {
            component: 'TransferPlatesModal',
            key: ModalButtonKey.DONE,
          },
        },
      ];
    }

    const chooseDestinationPlateForm = (
      <div>
        <FieldLabel label="Destination" />
        <FormGroup>
          <Row>
            <Col md={6}>
              <label>
                <input
                  type="radio"
                  checked={this.state.shouldCreatePlate}
                  onChange={(e: GenericEvent) => {
                    this._handleChangeShouldCreatePlate(e);
                  }}
                  value={CREATE_NEW_PLATE}
                />
                <span className="u-tiny-spacing-left">New plate</span>
              </label>
            </Col>
            <Col md={6}>
              <label>
                <input
                  type="radio"
                  checked={!this.state.shouldCreatePlate}
                  onChange={(e: GenericEvent) => {
                    this._handleChangeShouldCreatePlate(e);
                  }}
                  value={USE_EXISTING_PLATE}
                  {...getTestProps({
                    component: 'TransferPlatesModal',
                    element: 'useExistingPlateRadioButton',
                  })}
                />
                <span className="u-tiny-spacing-left">Existing plate</span>
              </label>
            </Col>
          </Row>
        </FormGroup>
        {this.state.shouldCreatePlate ? (
          <div className="well">
            <CreatePlatePane
              {...createPlatePaneState}
              onUpdate={(paneState: CreatePlatePaneState) => {
                this._handleUpdateCreatePlatePane(paneState);
              }}
              registryId={
                createPlatePaneState.registry ? createPlatePaneState.registry.id : this.props.registryId
              }
              spreadsheetUploadHelpText={UPLOAD_WELLS_HELP_TEXT}
            />
          </div>
        ) : (
          <>
            <div className="well">
              <ExistingPlatePane
                onUpdate={(paneState: ExistingPlatePaneState) => {
                  this._handleUpdateExistingPlatePane(paneState);
                }}
                registryId={this.props.registryId}
                selectedGrid={existingPlatePaneState.selectedGrid}
              />
            </div>
            {this.props.destinationPlateError != null && (
              <InlineBanner bannerType={InlineBannerType.ERROR} text={this.props.destinationPlateError} />
            )}
            {this.renderPossibleWarning('outputSampleStatusState')}
          </>
        )}
      </div>
    );

    return (
      <Modal
        buttons={buttons}
        onRequestClose={this.props.onRequestClose}
        title="Transfer plates"
        dataTestAttrs={{component: 'TransferPlatesModal', element: 'root'}}
      >
        {(() => {
          if (this.state.step === TransferPlatesModalStep.CHOOSE_PLATE) {
            return (
              <>
                {this.renderPossibleWarning('inputSampleStatusState')}
                {(!this.state.inputSampleStatusState.shouldShowStatusWarnings ||
                  this.state.inputSampleStatusState.canProceedOnSampleStatus) &&
                  chooseDestinationPlateForm}
                {!this.hasBlockingSampleStatusWarnings() && this.renderPossibleDestinationWarning()}
                {this.state.shouldShowNameTemplateBanner && (
                  <div className={css(styles.nameTemplateInfoBanner)}>
                    <NameTemplateInfoBanner itemDescription="container" isPlural={true} />
                  </div>
                )}
              </>
            );
          } else if (this.state.step === TransferPlatesModalStep.CONFIRM_QUANTITY) {
            return (
              <TransferPlatesQuantityPane
                cells={this.props.cells}
                onChangeShouldTransferAllQuantities={(e: GenericEvent) => {
                  this._handleChangeShouldTransferAllQuantities(e);
                }}
                onChangeTransferQuantity={(quantity: MeasurementConstants.LegacyMeasurement) => {
                  this._handleChangeTransferQuantity(quantity);
                }}
                {...transferPlatesQuantityPaneState}
                hasBlockingError={hasBlockingError}
                onChangeTransferQuantityErrorState={this._handleChangeTransferQuantityErrorState}
              />
            );
          } else {
            return (
              <ConfirmConcentrationsPane
                paneState={{...confirmConcentrationsPaneState, errorMessage: this.state.errorMessage}}
                onChangeConcentration={this._handleChangeConcentration}
                allowRecalculateConcentrations={false}
              />
            );
          }
        })()}
        {blockingErrorText && <BlockingError errorMessage={blockingErrorText} />}
      </Modal>
    );
  }
}

const ConnectedTransferPlatesModal = TransferPlatesModalConnector.connect(QueryingTransferPlatesModal);

const styles = StyleSheet.create({
  errorMessage: {paddingTop: '4px', whiteSpace: 'pre-line'},
  nameTemplateInfoBanner: {marginTop: 15},
});

export default ConnectedTransferPlatesModal;
